import React, { useState } from 'react';
import { Card, Typography, Tag, Row, Col, Divider, Table, Space, Tooltip, Button, message, Tabs } from 'antd';
import { ScissorOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { ResultComponentProps } from '../../common/types';
import { copyToClipboard } from '../../../../utils/clipboard';

const { Text, Paragraph, Title } = Typography;
const { TabPane } = Tabs;

// 区域颜色映射
const REGION_COLORS: Record<string, string> = {
  "signal_peptide": "#DC3912",  // 红色
  "mature": "#0099C6"           // 青色
};

// 区域类型中文名称
const REGION_TYPE_NAMES: Record<string, string> = {
  "signal_peptide": "信号肽",
  "mature": "成熟蛋白"
};

// 区域信息接口
interface Region {
  start: number;
  end: number;
  type: string;
  length: number;
}

// 单个序列结果组件
const SignalSequenceResult: React.FC<{
  result: any;
  artifacts: any;
  sequenceId: string;
}> = ({ result, artifacts, sequenceId }) => {
  // 兼容两种数据结构：统一格式的扁平字段（has_signal），或嵌套格式的旧字段名
  const hasSignal = result.has_signal ?? result.signal_peptide ?? result.has_signal_peptide;
  const cleavageSite = result.cleavage_site;
  const signalScore = result.signal_score || result.score;
  const signalSequence = result.signal_sequence;
  
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>是否有信号肽: </Text>
            {hasSignal ? (
              <Tag color="success">是</Tag>
            ) : (
              <Tag color="default">否</Tag>
            )}
          </div>
          {hasSignal && cleavageSite && (
            <div>
              <Text strong>切割位点: </Text>
              <Text>{cleavageSite}</Text>
            </div>
          )}
          {hasSignal && signalScore !== undefined && (
            <div>
              <Text strong>信号肽得分: </Text>
              <Text>{typeof signalScore === 'number' ? signalScore.toFixed(3) : signalScore}</Text>
            </div>
          )}
          {hasSignal && signalSequence && (
            <div>
              <Text strong>信号肽序列: </Text>
              <Text code style={{ backgroundColor: '#f0f0f0', padding: '2px 4px' }}>
                {signalSequence}
              </Text>
            </div>
          )}
        </Space>
      </Card>
    </Space>
  );
};

// 信号肽预测结果数据接口
interface SignalPeptideData {
  success: boolean;
  table_data: Array<{
    序列ID: string;
    是否有信号肽: string;
    信号肽起点: number | string;
    信号肽终点: number | string;
    信号肽序列: string;
    信号肽得分: number | string;
    信号肽长度: number | string;
    原始输入序列: string;
    原始输入序列长度: number | string;
  }>;
  raw_results: Array<{
    success: boolean;
    sequence: string;
    sequence_length: number;
    accession: string;
    has_signal: boolean;
    signal_type: string | null;
    signal_start: number | null;
    signal_end: number | null;
    signal_sequence: string | null;
    cleavage_site: number | null;
    signal_score: number | null;
    signal_length: number | null;
    regions: Region[];
  }>;
  result_count: number;
  error?: string;
  execution_time?: number;
}

// 信号肽预测结果组件
const SignalPeptideResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults = false,
  resultIndex
}) => {
  const [showFullSequence, setShowFullSequence] = useState(false);
  const [activeTab, setActiveTab] = useState('table');
  const [selectedSequenceIndex, setSelectedSequenceIndex] = useState(0);

  // 处理新的统一数据格式
  const processedData = React.useMemo(() => {
    console.log('🔍 SignalPeptideResult 接收到的数据:', {
      data,
      hasData: !!data,
      hasResults: !!(data && data.results),
      resultsIsArray: Array.isArray(data?.results),
      dataKeys: data ? Object.keys(data) : []
    });
    
    // 处理新的统一格式：{ results: [...], artifacts: {...} }
    if (data && data.results && Array.isArray(data.results)) {
      return {
        isUnified: true,
        results: data.results,
        artifacts: data.artifacts || {},
        meta: data.meta || {},
        total_sequences: data.total_sequences || data.results.length,
        successful_count: data.successful_count || 0,
        failed_count: data.failed_count || 0
      };
    }
    
    // 兼容旧格式
    return { isUnified: false, ...data };
  }, [data]);

  // 渲染多序列结果
  if (processedData.isUnified && processedData.results) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "信号肽预测结果"}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
      >
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>总序列数: </Text>
              <Text>{processedData.total_sequences}</Text>
            </div>
            <div>
              <Text strong>成功预测: </Text>
              <Text style={{ color: '#52c41a' }}>{processedData.successful_count}</Text>
            </div>
            <div>
              <Text strong>失败预测: </Text>
              <Text style={{ color: '#ff4d4f' }}>{processedData.failed_count}</Text>
            </div>
          </Space>
        </Card>

        {processedData.results.map((result: any, index: number) => (
          <Card key={index} style={{ marginBottom: 16 }} title={`序列: ${result.sequence_id}`}>
            {result.success ? (
              <SignalSequenceResult
                result={result.result || result}
                artifacts={processedData.artifacts}
                sequenceId={result.sequence_id}
              />
            ) : (
              <Text type="danger">预测失败: {result.error}</Text>
            )}
          </Card>
        ))}
      </BaseResultDisplay>
    );
  }

  // 如果数据加载失败 - 修复：统一格式不依赖success字段
  if (!processedData || processedData.error || (!processedData.isUnified && !processedData.success)) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "信号肽预测结果"}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
      >
        <Card>
          <Text type="danger">
            {processedData?.error || "预测失败，未获取到有效数据"}
          </Text>
        </Card>
      </BaseResultDisplay>
    );
  }

  // 提取信号肽预测结果数据
  const {
    table_data = [],
    raw_results = [],
    result_count = 0,
    execution_time = null
  } = processedData as SignalPeptideData;

  // 处理CSV导出
  const exportToCSV = () => {
    try {
      if (!table_data || table_data.length === 0) {
        message.error('没有可导出的数据');
        return;
      }

      // 获取表头
      const headers = Object.keys(table_data[0]);
      
      // 创建CSV内容
      let csvContent = headers.join(',') + '\n';
      
      // 添加数据行
      table_data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header as keyof typeof row];
          // 处理包含逗号的字段，用引号包裹
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value;
        });
        csvContent += values.join(',') + '\n';
      });
      
      // 创建Blob对象
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      // 创建下载链接并触发下载
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `signal_peptide_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('CSV文件导出成功');
    } catch (error) {
      console.error('导出CSV失败:', error);
      message.error('导出CSV失败');
    }
  };

  // 复制表格数据为TSV格式
  const copyTableAsText = () => {
    try {
      if (!table_data || table_data.length === 0) {
        message.error('没有可复制的数据');
        return;
      }

      // 获取表头
      const headers = Object.keys(table_data[0]);
      
      // 创建TSV内容
      let tsvContent = headers.join('\t') + '\n';
      
      // 添加数据行
      table_data.forEach(row => {
        const values = headers.map(header => row[header as keyof typeof row]);
        tsvContent += values.join('\t') + '\n';
      });
      
      if (copyToClipboard(tsvContent)) {
        message.success('表格数据已复制到剪贴板');
      } else {
        message.error('复制失败，请手动选择并复制');
      }
    } catch (error) {
      console.error('复制表格数据失败:', error);
      message.error('复制表格数据失败');
    }
  };

  // 处理序列复制
  const handleCopySequence = (text: string) => {
    if (copyToClipboard(text)) {
      message.success('序列已复制到剪贴板');
    } else {
      message.error('复制失败，请手动选择并复制');
    }
  };

  // 渲染高亮显示的序列
  const renderHighlightedSequence = (sequenceData: any) => {
    const { sequence, regions = [], cleavage_site = null } = sequenceData;
    
    if (!sequence) return <Text>无序列数据</Text>;
    
    const sequenceDisplay: JSX.Element[] = [];
    const maxDisplayLength = showFullSequence ? sequence.length : 200; // 最大显示长度
    
    // 构建区域映射
    const regionMap: Record<number, string> = {};
    regions.forEach((region: Region) => {
      for (let i = region.start; i <= region.end; i++) {
        regionMap[i] = region.type;
      }
    });
    
    // 逐字符渲染序列
    for (let i = 0; i < Math.min(sequence.length, maxDisplayLength); i++) {
      const position = i + 1; // 1-based position
      const regionType = regionMap[position];
      const isCleavageSite = position === cleavage_site;
      
      // 根据区域类型设置背景色
      let backgroundColor = 'transparent';
      let color = 'inherit';
      if (regionType && REGION_COLORS[regionType]) {
        backgroundColor = REGION_COLORS[regionType];
        color = 'white';
      }
      
      sequenceDisplay.push(
        <Tooltip 
          key={i} 
          title={
            <>
              <div>位置: {i + 1}</div>
              <div>区域: {regionType ? REGION_TYPE_NAMES[regionType] || regionType : '未分类'}</div>
              {isCleavageSite && <div><strong>信号肽切割位点</strong></div>}
            </>
          }
        >
          <span 
            style={{ 
              backgroundColor, 
              color, 
              padding: '2px 0', 
              fontFamily: 'monospace',
              borderBottom: isCleavageSite ? '2px dashed red' : 'none',
              position: 'relative'
            }}
          >
            {sequence[i]}
            {isCleavageSite && (
              <span style={{ 
                position: 'absolute', 
                top: '-12px', 
                right: '-2px', 
                fontSize: '12px', 
                color: 'red' 
              }}>
                <ScissorOutlined />
              </span>
            )}
          </span>
        </Tooltip>
      );
    }

    // 如果序列太长，显示省略号和"显示更多"按钮
    if (sequence.length > maxDisplayLength && !showFullSequence) {
      sequenceDisplay.push(
        <span key="more" style={{ marginLeft: '8px' }}>
          ... 
          <Button 
            type="link" 
            size="small" 
            onClick={() => setShowFullSequence(true)}
          >
            显示完整序列
          </Button>
        </span>
      );
    }

    return (
      <div style={{ 
        lineHeight: '1.8em', 
        wordBreak: 'break-all', 
        fontFamily: 'monospace',
        fontSize: '16px',
        letterSpacing: '1px'
      }}>
        {sequenceDisplay}
      </div>
    );
  };
  
  // 表格列定义
  const columns = [
    {
      title: '序列ID',
      dataIndex: '序列ID',
      key: '序列ID',
      fixed: 'left' as const,
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: '是否有信号肽',
      dataIndex: '是否有信号肽',
      key: '是否有信号肽',
      width: 120,
      render: (text: string) => (
        <Text strong type={text === '是' ? 'success' : 'secondary'}>
          {text}
        </Text>
      ),
    },
    {
      title: '信号肽起点',
      dataIndex: '信号肽起点',
      key: '信号肽起点',
      width: 100,
    },
    {
      title: '信号肽终点',
      dataIndex: '信号肽终点',
      key: '信号肽终点',
      width: 100,
    },
    {
      title: '信号肽长度',
      dataIndex: '信号肽长度',
      key: '信号肽长度',
      width: 100,
    },
    {
      title: '信号肽得分',
      dataIndex: '信号肽得分',
      key: '信号肽得分',
      width: 100,
      render: (score: number) => score ? score.toFixed(2) : '',
    },
    {
      title: '信号肽序列',
      dataIndex: '信号肽序列',
      key: '信号肽序列',
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          <Text style={{ fontFamily: 'monospace' }}>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: '序列长度',
      dataIndex: '原始输入序列长度',
      key: '原始输入序列长度',
      width: 100,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: any, index: number) => (
        <Button 
          type="link" 
          onClick={() => {
            setSelectedSequenceIndex(index);
            setActiveTab('detail');
          }}
        >
          详情
        </Button>
      ),
    },
  ];

  // 渲染单序列详情视图
  const renderDetailView = () => {
    if (!raw_results || raw_results.length === 0 || selectedSequenceIndex >= raw_results.length) {
      return <Text>没有可显示的详细信息</Text>;
    }

    const sequenceData = raw_results[selectedSequenceIndex];
    const { 
      accession, 
      has_signal, 
      signal_type, 
      signal_start, 
      signal_end, 
      signal_score, 
      signal_sequence, 
      cleavage_site,
      sequence,
      sequence_length
    } = sequenceData;

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Title level={5}>序列基本信息</Title>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Text strong>序列ID: </Text>
                <Text>{accession}</Text>
              </Col>
              <Col span={12}>
                <Text strong>序列长度: </Text>
                <Text>{sequence_length}</Text>
              </Col>
              <Col span={24}>
                <Text strong>是否含有信号肽: </Text>
                {has_signal ? (
                  <Tag color="green">是</Tag>
                ) : (
                  <Tag color="default">否</Tag>
                )}
              </Col>
              {has_signal && (
                <>
                  <Col span={12}>
                    <Text strong>信号肽类型: </Text>
                    <Text>{signal_type}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>信号肽位置: </Text>
                    <Text>{signal_start} - {signal_end}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>信号肽长度: </Text>
                    <Text>{signal_end && signal_start ? signal_end - signal_start + 1 : '-'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>信号肽预测分数: </Text>
                    <Text>{signal_score !== null ? signal_score.toFixed(2) : '-'}</Text>
                  </Col>
                  <Col span={24}>
                    <Text strong>信号肽序列: </Text>
                    <Text copyable style={{ fontFamily: 'monospace' }}>{signal_sequence}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>切割位点: </Text>
                    <Text>{cleavage_site}</Text>
                  </Col>
                </>
              )}
            </Row>
          </Space>
        </div>

        <Divider />

        <div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={5}>序列可视化</Title>
              <Button 
                icon={<CopyOutlined />} 
                onClick={() => handleCopySequence(sequence)}
              >
                复制序列
              </Button>
            </div>
            
            <div style={{ marginTop: 8 }}>
              {renderHighlightedSequence(sequenceData)}
            </div>

            <div style={{ marginTop: 16 }}>
              <Space>
                {Object.entries(REGION_TYPE_NAMES).map(([key, name]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: REGION_COLORS[key],
                        marginRight: 6
                      }}
                    />
                    <span>{name}</span>
                  </div>
                ))}
              </Space>
            </div>
          </Space>
        </div>
      </Space>
    );
  };

  // 渲染主要内容
  return (
    <BaseResultDisplay
      data={data}
      title={title || "信号肽预测结果"}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
    >
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Space>
              <Title level={4}>信号肽预测结果</Title>
              {execution_time && (
                <Text type="secondary">
                  (执行时间: {execution_time.toFixed(2)}秒)
                </Text>
              )}
            </Space>
            <Paragraph>
              共分析了 <Text strong>{result_count}</Text> 条序列，
              其中 <Text strong type="success">
                {table_data.filter(item => item.是否有信号肽 === '是').length}
              </Text> 条序列含有信号肽。
            </Paragraph>
          </div>

          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            type="card"
          >
            <TabPane tab="表格视图" key="table">
              <div style={{ textAlign: 'right', marginBottom: 16 }}>
                <Space>
                  <Button 
                    icon={<CopyOutlined />} 
                    onClick={copyTableAsText}
                  >
                    复制表格数据
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />} 
                    onClick={exportToCSV}
                  >
                    导出CSV
                  </Button>
                </Space>
              </div>
              
              <Table 
                dataSource={table_data}
                columns={columns}
                rowKey="序列ID"
                scroll={{ x: 'max-content' }}
                pagination={{ 
                  pageSize: 10, 
                  showSizeChanger: true, 
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `共 ${total} 条记录`
                }}
                size="small"
                bordered
                onRow={(record, index) => ({
                  onClick: () => {
                    if (index !== undefined) {
                      setSelectedSequenceIndex(index);
                      setActiveTab('detail');
                    }
                  },
                  style: { cursor: 'pointer' }
                })}
              />
            </TabPane>
            
            <TabPane tab="详细信息" key="detail">
              {renderDetailView()}
            </TabPane>
          </Tabs>

          <Card type="inner" title="关于DeepSig预测">
            <Paragraph>
              DeepSig是一个用于预测蛋白质信号肽的工具，它提供以下信息：
            </Paragraph>
            <ul>
              <li>是否含有信号肽</li>
              <li>信号肽的位置（起始和结束位置）</li>
              <li>信号肽序列</li>
              <li>预测分数</li>
            </ul>
            <Paragraph>
              本工具支持单条或多条序列（FASTA格式）输入，适用于大规模数据分析。
            </Paragraph>
          </Card>
        </Space>
      </Card>
    </BaseResultDisplay>
  );
};

export default SignalPeptideResult; 